import angular from "angular";

import { HolidayYears, HolidayPayCalculationReportData, IrRow, CalculationRowType, EnumerationsLogic, IncomeTypesLogic, HolidayPayCalculationReportDataCalc, Arrays, Numeric, ODataResult, ApiListItem } from "@salaxy/core";
import { UiHelpers } from "../../services";

/**
 * Provides functionality for building ad hoc holiday pay reports.
 */
export class HolidayPayReportQueryController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "HolidayYears",
    "UiHelpers",
    "$routeParams",
  ];

  /** Raw report from api */
  public data: HolidayPayCalculationReportData;

  /** Current employment id */
  public employmentId: string;

  /** Current holidayYear */
  public year: number;

  //** All holiday years for the worker */
  public years: string[] = [];

  public totals: HolidayPayCalculationReportDataCalc = {
     total: 0,
     hours: 0,
     overtimeHours: 0,
     workingDays: 0,
     absenceDays: 0
  };

  constructor(
    private holidayYearsApi: HolidayYears,
    private uiHelpers: UiHelpers,
    private $routeParams: angular.route.IRouteParamsService,

  ) {
  }

  /** Controller initialization */
  public $onInit() {
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
    const crudItemId = this.$routeParams.crudItemId ?? "";
    this.year = crudItemId.split("/")[0];
    this.employmentId = crudItemId.split("/")[1];
    this.loadYears().then( years => {
      this.years = years;
      this.query();
      loading.dismiss();
    });
  }


  /** Queries the report data. */
  public query() {
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
    this.holidayYearsApi.getHolidayPayCalculationReportData(this.year, this.employmentId)
      .then((result) => {
        this.data = result;
        // round accruals
        (this.data.holidayYear.accrual.total as any) = Numeric.round(this.data.holidayYear.accrual.total, 0);
        this.totals.total = Numeric.round(Arrays.sum(this.data.calculations, (x) => x.total), 2);
        this.totals.hours = Numeric.round(Arrays.sum(this.data.calculations, (x) => x.hours), 2);
        this.totals.overtimeHours = Numeric.round(Arrays.sum(this.data.calculations, (x) => x.overtimeHours), 2);
        this.totals.workingDays = Numeric.round(Arrays.sum(this.data.calculations, (x) => x.workingDays), 2);
        this.totals.absenceDays = Numeric.round(Arrays.sum(this.data.calculations, (x) => x.absenceDays), 2);
        loading.dismiss();
    });
  }

  /** Returns label for the ir row. */
  public getLabel(irRow: IrRow): string {

    //TODO: add this logic to UiHelpers.

    if (irRow.message) {
      return irRow.message;
    }
    let rowType = irRow.data.calculationRowType || CalculationRowType.Unknown;
    rowType = (rowType.substr(0, 1).toLowerCase() + rowType.substr(1)) as CalculationRowType;
    const code = irRow.irData?.code as number;
    const kind = irRow.data.kind as string;

    let label = "";
    if (rowType === CalculationRowType.IrIncomeType) {
      label = IncomeTypesLogic.getAll(null).find( (x) => (kind && x.transactionCode === kind) || (code > 0 && x.code === code))?.label;
    } else if (rowType === CalculationRowType.MealBenefit && code !== 334) {
      // Meal benefit deductions
      label = IncomeTypesLogic.getAll(null).find( (x) => code > 0 && x.code === code)?.label;
    } else if([
      CalculationRowType.Unknown,
      CalculationRowType.CarBenefit,
      CalculationRowType.BicycleBenefit,
      CalculationRowType.SubsidisedCommute,
      CalculationRowType.TaxAtSource,
      CalculationRowType.HourlySalaryWithWorkingTimeCompensation
    ].includes(rowType))
    {
      label = IncomeTypesLogic.getAll(null).find( (x) => code > 0 && x.code === code)?.label;
    }
    else if([
      CalculationRowType.DailyAllowance,
      CalculationRowType.EmploymentTermination,
      CalculationRowType.WorkingTimeCompensation,
      CalculationRowType.Remuneration,
      CalculationRowType.OtherCompensation,
      CalculationRowType.NonProfitOrg,
      CalculationRowType.MealBenefit, // main MealBenefit
      CalculationRowType.Training,
      CalculationRowType.PaidSickLeave,
      CalculationRowType.TimeRatePay,
    ].includes(rowType) && kind) {
      label =  EnumerationsLogic.getEnumLabel(`${irRow.data.calculationRowType}Kind`, kind, null, null);
    }
    return label || EnumerationsLogic.getEnumLabel("CalculationRowType", rowType, null, null);

  }

  private loadYears(): Promise<string[]> {
    return this.holidayYearsApi.getOData({$filter: `data/employmentId eq '${this.employmentId}'`}).then((data: ODataResult<ApiListItem>) => {
        const years = data.items.map((x) => "" + x.data.year);
        years.sort();
        years.reverse();
        return years;
      });
  }
}